import {
  Dialog,
  LabeledText,
  LabeledTextGroup,
  MessageBar,
  MessageBarType,
  Stack,
  SubmissionForm,
  TextInputField,
  useTheme
} from "@bps/fluent-ui";
import { Validator } from "@bps/validator";
import {
  ComparisonOperator,
  SelectorCreateRequestRule
} from "@libs/api/gateways/field/field-ops-gateway.dtos";

import { CommaSeparatedListValueCount } from "./CommaSeparatedListValueCount";

interface SelectorFormRuleListTypeValues {
  attributeType?: string;
  comparisonOperator?: string;
  value?: string;
}

const validator = new Validator<SelectorFormRuleListTypeValues>(validator => ({
  value: validator.string().required(),
  attributeType: validator.string().required()
}));

interface EditListAttributeValueDialogProps {
  onDismiss: () => void;
  rule: SelectorCreateRequestRule;
  update: (item: SelectorCreateRequestRule) => void;
}

export const EditListAttributeValueDialog: React.FC<EditListAttributeValueDialogProps> = ({
  onDismiss,
  update,
  rule
}) => {
  const theme = useTheme();

  const defaultValues = {
    attributeType: rule?.attributeType ?? "",
    comparisonOperator: rule?.comparisonOperator ?? ComparisonOperator.Equals,
    value: rule?.value?.replaceAll(",", "\n") ?? ""
  };

  const makeCommaSeparated = (value?: string) =>
    value?.trim()?.replaceAll("\n", ",") ?? "";

  const onSubmit = async (rule: SelectorFormRuleListTypeValues) => {
    const valueField = makeCommaSeparated(rule.value);
    update({
      ...rule,
      value: valueField,
      valueDataType: "LIST"
    });

    // Reset form and dismiss model.
    onDismiss();
  };

  return (
    <Dialog
      dialogContentProps={{
        showCloseButton: true,
        title: "Edit List Attribute"
      }}
      minWidth="55%"
      onDismiss={onDismiss}
      hidden={false}
    >
      <SubmissionForm<SelectorFormRuleListTypeValues>
        initialValues={defaultValues}
        onSubmit={onSubmit}
        validate={validator.validate}
        cancelButtonProps={{ onClick: onDismiss }}
      >
        {() => {
          return (
            <Stack verticalFill tokens={{ childrenGap: theme.spacing.s1 }}>
              <Stack tokens={{ childrenGap: theme.spacing.m }}>
                <LabeledTextGroup
                  horizontalColumnCount={5}
                  styles={{
                    root: {
                      alignItems: "start"
                    }
                  }}
                >
                  <LabeledText label="Attribute name">
                    {rule?.attributeType}
                  </LabeledText>
                  <LabeledText label="Operator">
                    {rule?.comparisonOperator}
                  </LabeledText>
                </LabeledTextGroup>

                <MessageBar messageBarType={MessageBarType.info}>
                  Enter values as separate lines without comma separation. The
                  rule will evaluate as true if a device property matches any of
                  the supplied values.
                </MessageBar>
              </Stack>

              <TextInputField
                label="Value"
                required
                name="value"
                styles={{
                  root: { width: "100%" },
                  fieldGroup: { minHeight: "300px" }
                }}
                multiline={true}
              />

              <CommaSeparatedListValueCount value={rule.value} />
            </Stack>
          );
        }}
      </SubmissionForm>
    </Dialog>
  );
};
